package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*

data class TableColumn<T>(
    val header: String,
    val weight: Float = 1f,
    val align: Alignment.Vertical = Alignment.CenterVertically,
    /** When set, the header becomes clickable and sorts the loaded rows by this key. */
    val sortKey: ((T) -> Comparable<*>?)? = null,
    val cell: @Composable RowScope.(T) -> Unit
)

@Composable
fun <T> DataTable(
    items: List<T>,
    columns: List<TableColumn<T>>,
    modifier: Modifier = Modifier,
    keyOf: (T) -> Any? = { it },
    onRowClick: ((T) -> Unit)? = null,
    emptyText: String = "No data",
    loading: Boolean = false,
    /** Index of the column to sort by on first render (-1 = none). */
    defaultSortIndex: Int = -1,
    /** If true, default sort is ascending; if false, descending. */
    defaultSortAsc: Boolean = false,
) {
    // Sorting only reorders the rows already loaded on this page — the
    // backend has no sort params, so columns opt in via sortKey only where
    // the table holds the full dataset.
    var sortIndex by remember { mutableStateOf(defaultSortIndex) }
    var sortAsc by remember { mutableStateOf(defaultSortAsc) }

    val sorted = remember(items, sortIndex, sortAsc) {
        val key = columns.getOrNull(sortIndex)?.sortKey
        if (key == null) items
        else {
            // Nulls always sink to the bottom regardless of direction —
            // a plain comparator.reversed() would surface missing values on DESC.
            val comparator = Comparator<T> { a, b ->
                @Suppress("UNCHECKED_CAST")
                val ka = key(a) as? Comparable<Any?>
                @Suppress("UNCHECKED_CAST")
                val kb = key(b) as? Comparable<Any?>
                when {
                    ka == null && kb == null -> 0
                    ka == null -> 1
                    kb == null -> -1
                    else -> if (sortAsc) ka.compareTo(kb) else kb.compareTo(ka)
                }
            }
            items.sortedWith(comparator)
        }
    }

    Box(modifier.fillMaxWidth().classNames("na-table-scroll")) {
    Column(Modifier.fillMaxWidth()) {
        // Dense header — 10px uppercase letter-spaced eyebrow labels
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 20.px, topBottom = 10.px)
                .styleModifier {
                    property("border-bottom", "1px solid #1C2634")
                    property("min-height", "36px")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            columns.forEachIndexed { index, col ->
                val sortable = col.sortKey != null
                val active = index == sortIndex
                Box(
                    Modifier
                        .weight(col.weight)
                        .padding(right = 12.px)
                        .then(if (sortable) Modifier.cursor(Cursor.Pointer) else Modifier)
                        .styleModifier {
                            property("min-width", "0")
                            property("flex-basis", "0")
                            property("overflow", "hidden")
                        }
                        .then(
                            if (sortable) Modifier.onClick {
                                // desc → asc → off: admins usually want "biggest first".
                                when {
                                    !active -> { sortIndex = index; sortAsc = false }
                                    !sortAsc -> sortAsc = true
                                    else -> sortIndex = -1
                                }
                            } else Modifier
                        )
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            col.header,
                            Modifier
                                .eyebrow()
                                .color(if (active) AppColors.Ink else AppColors.Faint)
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("text-overflow", "ellipsis")
                                    property("overflow", "hidden")
                                }
                        )
                        if (sortable) {
                            Box(Modifier.width(5.px))
                            SpanText(
                                if (!active) "↕" else if (sortAsc) "↑" else "↓",
                                Modifier.fontSize(10.px)
                                    .color(if (active) AppColors.Ink else AppColors.Faint)
                            )
                        }
                    }
                }
            }
        }
        if (loading) {
            repeat(SKELETON_ROWS) { index ->
                SkeletonRow(columns, isLast = index == SKELETON_ROWS - 1)
            }
        } else if (items.isEmpty()) {
            Box(Modifier.fillMaxWidth().padding(topBottom = 48.px), contentAlignment = Alignment.Center) {
                SpanText(emptyText, Modifier.bodyMRegular().color(AppColors.Faint))
            }
        } else {
            sorted.forEachIndexed { index, item ->
                TableRow(
                    item = item,
                    columns = columns,
                    isLast = index == sorted.size - 1,
                    onClick = onRowClick
                )
            }
        }
    }
    } // na-table-scroll Box
}

private const val SKELETON_ROWS = 6

// Cell widths cycle so the shimmer grid doesn't look like a uniform brick wall.
private val SKELETON_WIDTHS = listOf(72, 48, 60, 38, 54, 66)

@Composable
private fun <T> SkeletonRow(columns: List<TableColumn<T>>, isLast: Boolean) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 20.px, topBottom = 11.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid #1C2634")
                property("min-height", "36px")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        columns.forEachIndexed { index, col ->
            Box(
                Modifier
                    .weight(col.weight)
                    .padding(right = 12.px)
                    .styleModifier {
                        property("min-width", "0")
                        property("flex-basis", "0")
                    }
            ) {
                Box(
                    Modifier
                        .height(13.px)
                        .sBorderRadius(6)
                        .styleModifier {
                            property("width", "${SKELETON_WIDTHS[index % SKELETON_WIDTHS.size]}%")
                            property("background", "rgba(255,255,255,0.06)")
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
            }
        }
    }
}

@Composable
private fun <T> TableRow(
    item: T,
    columns: List<TableColumn<T>>,
    isLast: Boolean,
    onClick: ((T) -> Unit)?
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 20.px, topBottom = 8.px)
            .classNames("arena-table-row")
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid #1C2634")
                // Dense 36px rows; hover shows #101828 via CSS
                property("min-height", "36px")
                property("transition", "background 140ms ease")
            }
            .then(
                if (onClick != null) Modifier.onClick { onClick(item) } else Modifier
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        columns.forEach { col ->
            Box(
                Modifier
                    .weight(col.weight)
                    .padding(right = 12.px)
                    .styleModifier {
                        property("min-width", "0")
                        property("flex-basis", "0")
                        property("overflow", "hidden")
                    }
            ) {
                Row(
                    Modifier.fillMaxWidth().styleModifier {
                        property("white-space", "nowrap")
                        property("text-overflow", "ellipsis")
                        property("overflow", "hidden")
                    },
                    verticalAlignment = col.align
                ) {
                    col.cell(this, item)
                }
            }
        }
    }
}


